import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, uploadImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useLang } from "@/context/LangContext";
import { FEATURE_LABELS } from "@/lib/planFeatures";
import { ArrowLeft, Save, Plus, Trash2, Tag, DollarSign, Clock, Type, Image as ImageIcon, Upload, SlidersHorizontal, MapPin } from "lucide-react";

const PLAN_KEYS = ["basic", "pro", "enterprise"];
const PLAN_LABEL = { es: { basic: "Básico", pro: "Pro", enterprise: "Empresarial" }, en: { basic: "Basic", pro: "Pro", enterprise: "Enterprise" } };
const FEAT_TXT = {
  es: { title: "Funciones por plan", desc: "Activa o desactiva las funciones incluidas en cada suscripción. Los cambios aplican de inmediato a los negocios de ese plan.", col: "Función" },
  en: { title: "Features by plan", desc: "Turn features on or off for each subscription. Changes apply immediately to businesses on that plan.", col: "Feature" },
};

export default function AdminSettings() {
  const [cfg, setCfg] = useState(null);
  const [prices, setPrices] = useState({});
  const [trialDays, setTrialDays] = useState(14);
  const [trialPlan, setTrialPlan] = useState("pro");
  const [brandName, setBrandName] = useState("URBIS USA");
  const [logoUrl, setLogoUrl] = useState("");
  const [faviconUrl, setFaviconUrl] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [featKeys, setFeatKeys] = useState([]);
  const [featDefaults, setFeatDefaults] = useState({});
  const [featOverrides, setFeatOverrides] = useState({});
  const [addons, setAddons] = useState({ multi_location: { packages: [] } });
  const [uploading, setUploading] = useState("");
  const [discounts, setDiscounts] = useState([]);
  const [nd, setNd] = useState({ code: "", percent: 10, start_date: "", end_date: "" });
  const { lang } = useLang();

  const loadCfg = () => api.get("/admin/config").then((r) => {
    setCfg(r.data);
    const p = {};
    PLAN_KEYS.forEach((k) => { p[k] = { price: r.data.plans[k].price, commission: r.data.plans[k].commission }; });
    setPrices(p); setTrialDays(r.data.trial_days ?? 14); setTrialPlan(r.data.trial_plan || "pro"); setBrandName(r.data.brand_name || "URBIS USA"); setLogoUrl(r.data.logo_url || ""); setFaviconUrl(r.data.favicon_url || ""); setSiteUrl(r.data.site_url || ""); setAdminEmail(r.data.admin_notify_email || "");
    setFeatKeys(r.data.feature_keys || []); setFeatDefaults(r.data.feature_defaults || {}); setFeatOverrides(r.data.feature_overrides || {}); setAddons(r.data.addons || { multi_location: { packages: [] } });
  }).catch(() => {});
  const loadDiscounts = () => api.get("/admin/discounts").then((r) => setDiscounts(r.data)).catch(() => {});
  useEffect(() => { loadCfg(); loadDiscounts(); }, []);

  const saveCfg = async () => {
    try {
      await api.put("/admin/config", { plan_prices: prices, trial_days: Number(trialDays), trial_plan: trialPlan, brand_name: brandName, logo_url: logoUrl, favicon_url: faviconUrl, site_url: siteUrl, admin_notify_email: adminEmail, feature_overrides: featOverrides, addons });
      localStorage.removeItem("brand_name"); localStorage.removeItem("brand_logo");
      toast.success("Configuración guardada. Recargando…");
      setTimeout(() => window.location.reload(), 900);
    }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const setP = (k, field, v) => setPrices((prev) => ({ ...prev, [k]: { ...prev[k], [field]: v } }));
  const setPkg = (i, field, v) => setAddons((prev) => {
    const packages = (prev.multi_location?.packages || []).map((p, idx) => (idx === i ? { ...p, [field]: v } : p));
    return { ...prev, multi_location: { ...prev.multi_location, packages } };
  });
  const featOn = (plan, key) => {
    const ov = featOverrides[plan] || {};
    if (key in ov) return !!ov[key];
    return !!(featDefaults[plan] && featDefaults[plan][key]);
  };
  const toggleFeat = (plan, key) => setFeatOverrides((prev) => ({ ...prev, [plan]: { ...(prev[plan] || {}), [key]: !featOn(plan, key) } }));
  const handleUpload = async (e, setter, kind) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(kind);
    try { const url = await uploadImage(f); setter(url); toast.success("Imagen subida"); }
    catch { toast.error("Error al subir la imagen"); }
    finally { setUploading(""); e.target.value = ""; }
  };

  const addDiscount = async () => {
    if (!nd.code.trim()) return toast.error("Ingresa un código");
    try { await api.post("/admin/discounts", { ...nd, percent: Number(nd.percent) }); toast.success("Descuento creado"); setNd({ code: "", percent: 10, start_date: "", end_date: "" }); loadDiscounts(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delDiscount = async (code) => { await api.delete(`/admin/discounts/${code}`); loadDiscounts(); };

  if (!cfg) return <div className="p-10 text-center text-slate-400">…</div>;

  return (
    <div className="mx-auto max-w-4xl px-6 py-10" data-testid="admin-settings-page">
      <a href="/admin" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-secondary"><ArrowLeft className="h-4 w-4" /> Volver al panel</a>
      <h1 className="font-head text-3xl font-extrabold tracking-tight text-secondary">Configuración comercial</h1>

      <section className="mt-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="branding-section">
        <h2 className="flex items-center gap-2 font-head text-lg font-bold text-secondary"><Type className="h-5 w-5 text-primary" /> Marca de la aplicación</h2>
        <p className="mt-1 text-sm text-slate-500">Este nombre aparece en la barra superior, el panel, la pestaña del navegador, las facturas y los correos.</p>
        <div className="mt-3 max-w-sm">
          <Label className="text-xs font-bold uppercase text-slate-500">Nombre de la app</Label>
          <Input value={brandName} onChange={(e) => setBrandName(e.target.value)} className="mt-1 rounded-sm" data-testid="brand-name-input" placeholder="URBIS USA" />
        </div>
        <div className="mt-3 max-w-sm">
          <Label className="text-xs font-bold uppercase tracking-normal text-slate-500">Enlace del sitio&nbsp;(facturas / QR)</Label>
          <Input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} className="mt-1 rounded-sm" data-testid="brand-site-input" placeholder="https://urbis.shop" />
        </div>
        <div className="mt-3 max-w-sm">
          <Label className="text-xs font-bold uppercase tracking-normal text-slate-500">Email para avisos de leads</Label>
          <Input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} className="mt-1 rounded-sm" data-testid="admin-notify-email-input" placeholder="tucorreo@gmail.com" />
          <p className="mt-1 text-xs text-slate-400">Aquí llegarán los avisos por correo cuando un cliente contacte al negocio maestro.</p>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <Label className="text-xs font-bold uppercase text-slate-500">Logotipo</Label>
            <div className="mt-2 flex items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt="logo" className="h-12 w-auto max-w-[160px] rounded border border-slate-200 bg-secondary object-contain p-1" data-testid="logo-preview" />
              ) : (
                <span className="flex h-12 w-12 items-center justify-center rounded border border-dashed border-slate-300 text-slate-300"><ImageIcon className="h-5 w-5" /></span>
              )}
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-primary hover:text-primary">
                <Upload className="h-4 w-4" /> {uploading === "logo" ? "Subiendo…" : "Subir logo"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e, setLogoUrl, "logo")} data-testid="logo-upload-input" />
              </label>
              {logoUrl && <button onClick={() => setLogoUrl("")} className="text-xs font-semibold text-slate-400 hover:text-destructive" data-testid="logo-remove-btn">Quitar</button>}
            </div>
            <p className="mt-1 text-xs text-slate-400">PNG con fondo transparente recomendado. Aparece en la barra superior, panel y correos.</p>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-normal text-slate-500">Icono del sitio&nbsp;(favicon)</Label>
            <div className="mt-2 flex items-center gap-3">
              {faviconUrl ? (
                <img src={faviconUrl} alt="favicon" className="h-10 w-10 rounded border border-slate-200 object-contain p-1" data-testid="favicon-preview" />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded border border-dashed border-slate-300 text-slate-300"><ImageIcon className="h-4 w-4" /></span>
              )}
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-primary hover:text-primary">
                <Upload className="h-4 w-4" /> {uploading === "favicon" ? "Subiendo…" : "Subir favicon"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e, setFaviconUrl, "favicon")} data-testid="favicon-upload-input" />
              </label>
              {faviconUrl && <button onClick={() => setFaviconUrl("")} className="text-xs font-semibold text-slate-400 hover:text-destructive" data-testid="favicon-remove-btn">Quitar</button>}
            </div>
            <p className="mt-1 text-xs text-slate-400">Imagen cuadrada (ej. 512×512). Aparece en la pestaña del navegador.</p>
          </div>
        </div>

        <p className="mt-3 text-xs text-slate-400">Se guarda junto con precios y prueba al pulsar el botón de abajo.</p>
      </section>

      <section className="mt-6 rounded-sm border border-slate-200 bg-white p-6">
        <h2 className="flex items-center gap-2 font-head text-lg font-bold text-secondary"><DollarSign className="h-5 w-5 text-primary" /> Precios de suscripción</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {PLAN_KEYS.map((k) => (
            <div key={k} className="rounded-sm border border-slate-200 p-4" data-testid={`price-card-${k}`}>
              <p className="font-head font-bold text-secondary">{PLAN_LABEL[k]}</p>
              <Label className="mt-2 block text-xs font-bold uppercase text-slate-500">Precio ($/mes)</Label>
              <Input type="number" step="0.01" value={prices[k]?.price ?? 0} onChange={(e) => setP(k, "price", Number(e.target.value))} className="mt-1 rounded-sm" data-testid={`price-input-${k}`} />
              <Label className="mt-2 block text-xs font-bold uppercase text-slate-500">Comisión (%)</Label>
              <Input type="number" step="0.1" value={prices[k]?.commission ?? 0} onChange={(e) => setP(k, "commission", Number(e.target.value))} className="mt-1 rounded-sm" data-testid={`commission-input-${k}`} />
            </div>
          ))}
        </div>
      </section>

      <section className="mt-6 rounded-sm border border-slate-200 bg-white p-6">
        <h2 className="flex items-center gap-2 font-head text-lg font-bold text-secondary"><Clock className="h-5 w-5 text-primary" /> Prueba gratis</h2>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div><Label className="text-xs font-bold uppercase text-slate-500">Días de prueba</Label><Input type="number" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} className="mt-1 w-28 rounded-sm" data-testid="trial-days-input" /></div>
          <div><Label className="text-xs font-bold uppercase text-slate-500">Plan de prueba</Label>
            <select value={trialPlan} onChange={(e) => setTrialPlan(e.target.value)} className="mt-1 block h-10 rounded-sm border border-input px-3 text-sm" data-testid="trial-plan-select">
              {PLAN_KEYS.map((k) => <option key={k} value={k}>{PLAN_LABEL[k]}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="plan-features-section">
        <h2 className="flex items-center gap-2 font-head text-lg font-bold text-secondary"><SlidersHorizontal className="h-5 w-5 text-primary" /> {FEAT_TXT[lang].title}</h2>
        <p className="mt-1 text-sm text-slate-500">{FEAT_TXT[lang].desc}</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-wider text-slate-400">
                <th className="py-2 pr-4">{FEAT_TXT[lang].col}</th>
                {PLAN_KEYS.map((p) => <th key={p} className="px-3 py-2 text-center">{PLAN_LABEL[lang][p]}</th>)}
              </tr>
            </thead>
            <tbody>
              {(featKeys.length ? featKeys : Object.keys(FEATURE_LABELS)).map((key) => (
                <tr key={key} className="border-b border-slate-100" data-testid={`feature-row-${key}`}>
                  <td className="py-2.5 pr-4 font-semibold text-secondary">{FEATURE_LABELS[key] ? FEATURE_LABELS[key][lang] : key}</td>
                  {PLAN_KEYS.map((p) => (
                    <td key={p} className="px-3 py-2.5 text-center">
                      <div className="flex justify-center">
                        <Switch checked={featOn(p, key)} onCheckedChange={() => toggleFeat(p, key)} data-testid={`feature-${p}-${key}`} />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="addon-multi-location-section">
        <h2 className="flex items-center gap-2 font-head text-lg font-bold text-secondary"><MapPin className="h-5 w-5 text-primary" /> Complemento Multi-Sucursal</h2>
        <p className="mt-1 text-sm text-slate-500">Disponible como add-on para cualquier plan. Define el precio mensual y anual de cada paquete de sucursales.</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {(addons.multi_location?.packages || []).map((p, i) => (
            <div key={p.size} className="rounded-sm border border-slate-200 p-4" data-testid={`addon-pkg-${p.size}`}>
              <p className="font-head font-bold text-secondary">{p.size} sucursales</p>
              <Label className="mt-2 block text-xs font-bold uppercase text-slate-500">Precio mensual ($)</Label>
              <Input type="number" step="0.01" value={p.monthly ?? 0} onChange={(e) => setPkg(i, "monthly", Number(e.target.value))} className="mt-1 rounded-sm" data-testid={`addon-monthly-${p.size}`} />
              <Label className="mt-2 block text-xs font-bold uppercase text-slate-500">Precio anual ($)</Label>
              <Input type="number" step="0.01" value={p.yearly ?? 0} onChange={(e) => setPkg(i, "yearly", Number(e.target.value))} className="mt-1 rounded-sm" data-testid={`addon-yearly-${p.size}`} />
            </div>
          ))}
        </div>
      </section>

      <Button onClick={saveCfg} className="mt-4 rounded-sm bg-primary hover:bg-orange-600" data-testid="save-config-btn"><Save className="mr-2 h-4 w-4" /> Guardar marca, precios y prueba</Button>

      <section className="mt-8 rounded-sm border border-slate-200 bg-white p-6">
        <h2 className="flex items-center gap-2 font-head text-lg font-bold text-secondary"><Tag className="h-5 w-5 text-primary" /> Descuentos por temporada</h2>
        <div className="mt-3 flex flex-wrap items-end gap-2" data-testid="discount-form">
          <div><Label className="text-xs font-bold uppercase text-slate-500">Código</Label><Input value={nd.code} onChange={(e) => setNd({ ...nd, code: e.target.value })} className="mt-1 w-36 rounded-sm" data-testid="nd-code" /></div>
          <div><Label className="text-xs font-bold uppercase text-slate-500">%</Label><Input type="number" value={nd.percent} onChange={(e) => setNd({ ...nd, percent: e.target.value })} className="mt-1 w-20 rounded-sm" data-testid="nd-percent" /></div>
          <div><Label className="text-xs font-bold uppercase text-slate-500">Inicio</Label><Input type="date" value={nd.start_date} onChange={(e) => setNd({ ...nd, start_date: e.target.value })} className="mt-1 rounded-sm" data-testid="nd-start" /></div>
          <div><Label className="text-xs font-bold uppercase text-slate-500">Fin</Label><Input type="date" value={nd.end_date} onChange={(e) => setNd({ ...nd, end_date: e.target.value })} className="mt-1 rounded-sm" data-testid="nd-end" /></div>
          <Button onClick={addDiscount} className="rounded-sm bg-accent hover:bg-emerald-600" data-testid="add-discount-btn"><Plus className="mr-1 h-4 w-4" /> Crear</Button>
        </div>
        <div className="mt-4 divide-y divide-slate-100">
          {discounts.map((d) => (
            <div key={d.code} className="flex items-center justify-between py-2.5 text-sm" data-testid={`discount-${d.code}`}>
              <span><b className="text-secondary">{d.code}</b> · {d.percent}% <span className="text-slate-400">{d.start_date || "—"} → {d.end_date || "—"}</span></span>
              <button onClick={() => delDiscount(d.code)} className="text-slate-300 hover:text-destructive" data-testid={`del-discount-${d.code}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {discounts.length === 0 && <p className="py-3 text-center text-slate-400">Sin descuentos.</p>}
        </div>
      </section>
    </div>
  );
}
